import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { CloseIcon } from '@/components/Icons'

/**
 * Spec 08 · Bag drawer — what shadcn calls a Sheet.
 *
 * 420px right drawer at "raised" elevation over a 0.55 scrim with 2px
 * blur, z 50, 320ms slide. Below 560 the same component goes
 * full-screen (spec 13: "Bag drawer → full-screen sheet"), which is
 * the `full` side.
 */

const Sheet = SheetPrimitive.Root
const SheetTrigger = SheetPrimitive.Trigger
const SheetClose = SheetPrimitive.Close
const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      'sss-scrim fixed inset-0 z-40',
      'data-[state=open]:animate-[sssFadeIn_320ms_var(--sss-ease)]',
      'data-[state=closed]:animate-[sssFadeOut_320ms_var(--sss-ease-exit)]',
      className
    )}
    {...props}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  'fixed z-50 flex flex-col bg-background text-foreground shadow-raised focus:outline-none',
  {
    variants: {
      side: {
        right: [
          'inset-y-0 right-0 w-[420px] max-w-full',
          'data-[state=open]:animate-[sssSlideInRight_320ms_var(--sss-ease)]',
          'data-[state=closed]:animate-[sssSlideOutRight_320ms_var(--sss-ease-exit)]',
        ].join(' '),
        /** Full-screen sheet — mobile bag and the menu overlay. */
        full: [
          'inset-0 w-full',
          'data-[state=open]:animate-[sssFadeIn_200ms_var(--sss-ease)]',
          'data-[state=closed]:animate-[sssFadeOut_200ms_var(--sss-ease-exit)]',
        ].join(' '),
      },
    },
    defaultVariants: { side: 'right' },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /** Renders the overlay. Off for `full`, which covers the page itself. */
  overlay?: boolean
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', overlay = true, className, children, ...props }, ref) => (
  <SheetPortal>
    {overlay && <SheetOverlay />}
    <SheetPrimitive.Content
      ref={ref}
      tabIndex={-1}
      className={cn(sheetVariants({ side }), className)}
      // Radix focuses the first control on open, which lands a focus
      // ring on the close button before the user has touched the
      // keyboard. Focus the panel instead; the ring is for navigating,
      // not for arriving. A caller-supplied handler still wins.
      onOpenAutoFocus={(event) => {
        event.preventDefault()
        ;(event.currentTarget as HTMLElement | null)?.focus?.()
      }}
      {...props}
    >
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

/**
 * Header row — 16px pad, hairline bottom, label left, close right.
 * The close control keeps a 44×44 tap box even though the glyph is 16.
 */
function SheetHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-between gap-4 border-b border-hairline px-4 py-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      className={cn('text-[16px] font-semibold leading-none', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      className={cn('text-[12px] leading-[1.55] text-ink-muted', className)}
      {...props}
    />
  )
}

function SheetCloseButton({ label = 'Close' }: { label?: string }) {
  return (
    <SheetClose
      aria-label={label}
      className="-mr-2.5 flex size-11 cursor-pointer items-center justify-center text-ink transition-colors duration-[120ms] hover:text-ink-muted focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_5px_var(--sss-ring)]"
    >
      <CloseIcon size={18} />
    </SheetClose>
  )
}

/** Footer — hairline top, 14/16 pad, sits above the home indicator. */
function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'shrink-0 border-t border-hairline px-4 pt-3.5 pb-4 max-[559px]:pb-[22px]',
        className
      )}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetCloseButton,
  SheetFooter,
}
